import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getStayReport } from "../../server/Api";
import HotelRoomPreviewModal from "../../components/HotelRoomPreviewModal";
import {
  getBlockCategoryTitle,
  getBlockGroupKey,
} from "../../utils/hotelRoomUtils";
import {
  exportRowsToExcel,
  formatDate,
  formatMoney,
  formatSpendList,
  safeFileLabel,
} from "./reportShared";
import "../../assets/css/occasion.css";
import "../../assets/css/StayReports.css";

const mapStayHotelToPreview = (hotel) => ({
  _id: hotel.hotelId,
  hotelName: hotel.hotelName,
  totalNights: hotel.totalNights || 1,
  metrics: {
    totalRooms: hotel.totalRooms ?? 0,
    occupied: hotel.occupied ?? 0,
    available: hotel.available ?? 0,
  },
  rooms: (hotel.rooms || []).map((room) => ({
    roomNumber: room.roomNumber,
    roomType: room.roomType || "",
    bedType: room.bedType || "",
    currency: room.currency || "INR",
    price: room.nightlyRate ?? room.price ?? "",
    status: room.status === "occupied" ? "occupied" : "available",
    guestName: room.guestName || "",
  })),
});

const VIEW_TABS = [
  { id: "rooms", label: "Room Inventory", icon: "bi-door-open" },
  { id: "guests", label: "Guest Coverage", icon: "bi-people" },
];

const GUEST_FILTERS = [
  { id: "all", label: "All" },
  { id: "with", label: "With Stay" },
  { id: "without", label: "Without Stay" },
];

const ROOM_TYPE_ORDER = [
  "Standard",
  "Deluxe",
  "Premium",
  "Executive",
  "Suite",
];

/** Sum numeric room fields by currency → "₹1,200 · $90" */
const sumMoneyByCurrency = (rooms = [], field) => {
  const totals = {};
  rooms.forEach((room) => {
    const amount = Number(room?.[field]);
    if (!Number.isFinite(amount)) return;
    const currency = String(room.currency || "INR").toUpperCase() || "INR";
    totals[currency] = (totals[currency] || 0) + amount;
  });
  const list = Object.entries(totals).map(([currency, amount]) => ({
    currency,
    amount: Math.round(amount * 100) / 100,
  }));
  return formatSpendList(list);
};

const normalizeStayStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const isCheckedInStatus = (status) =>
  normalizeStayStatus(status) === "checked-in";

const isCheckedOutStatus = (status) =>
  normalizeStayStatus(status) === "checked-out";

const displayStayStatus = (status) => {
  const lower = normalizeStayStatus(status);
  if (!lower || lower === "pending" || lower === "confirmed") return "Assigned";
  if (lower === "checked-in") return "Checked-in";
  if (lower === "checked-out" || lower === "checked out") return "Checked-Out";
  if (lower === "cancelled" || lower === "canceled") return "Cancelled";
  if (lower === "assigned") return "Assigned";
  return status || "";
};

const sortRoomTypeGroups = (groups) =>
  [...groups].sort((a, b) => {
    const aType = String(a.roomType || "").trim();
    const bType = String(b.roomType || "").trim();
    const aIdx = ROOM_TYPE_ORDER.indexOf(aType);
    const bIdx = ROOM_TYPE_ORDER.indexOf(bType);
    const aOrder = aIdx === -1 ? ROOM_TYPE_ORDER.length : aIdx;
    const bOrder = bIdx === -1 ? ROOM_TYPE_ORDER.length : bIdx;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.label).localeCompare(String(b.label));
  });

const groupRoomsByType = (rooms = []) => {
  const groups = new Map();
  rooms.forEach((room) => {
    const roomType = String(room.roomType || "").trim();
    const bedType = String(room.bedType || room.bedSize || "").trim();
    const key = getBlockGroupKey(roomType, bedType);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        roomType,
        bedType,
        label: getBlockCategoryTitle(roomType, bedType),
        items: [],
      });
    }
    groups.get(key).items.push(room);
  });
  return sortRoomTypeGroups(Array.from(groups.values())).map((group) => ({
    ...group,
    rateTotalLabel: sumMoneyByCurrency(group.items, "nightlyRate"),
    valueTotalLabel: sumMoneyByCurrency(group.items, "roomNightValue"),
  }));
};

const MetricCard = ({ label, value, hint, tone }) => (
  <article className={`stay-report-metric ${tone || ""}`}>
    <span className="stay-report-metric-label">{label}</span>
    <strong className="stay-report-metric-value">{value}</strong>
    {hint ? <em className="stay-report-metric-hint">{hint}</em> : null}
  </article>
);

const StayReports = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("rooms");
  /** null | { viewMode: "single" | "event", ... } — same shape as Hotel Master */
  const [preview, setPreview] = useState(null);
  const [roomFilter, setRoomFilter] = useState("all");
  const [guestFilter, setGuestFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const loadReport = useCallback(async () => {
    if (!selectedEvent?.eventId) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getStayReport(selectedEvent.eventId));
      setReport(res.data?.data || null);
    } catch (error) {
      console.error("Error loading stay report:", error);
      toast.error(
        error.response?.data?.message || "Failed to load stay report"
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.eventId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    setPreview(null);
    setRoomFilter("all");
    setGuestFilter("all");
    setSearchTerm("");
  }, [selectedEvent?.eventId]);

  const summary = report?.summary;
  const hotels = report?.hotels || [];

  const roomRows = useMemo(() => {
    const source = hotels.flatMap((hotel) =>
      (hotel.rooms || []).map((room) => ({
        ...room,
        hotelName: hotel.hotelName,
        hotelId: hotel.hotelId,
      }))
    );

    return source.filter((room) => {
      if (roomFilter === "available" && room.status !== "available") return false;
      if (roomFilter === "occupied" && room.status !== "occupied") return false;
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return (
        String(room.roomNumber || "").toLowerCase().includes(q) ||
        String(room.hotelName || "").toLowerCase().includes(q) ||
        String(room.guestName || "").toLowerCase().includes(q) ||
        String(room.roomType || "").toLowerCase().includes(q)
      );
    });
  }, [hotels, roomFilter, searchTerm]);

  const roomGroups = useMemo(() => groupRoomsByType(roomRows), [roomRows]);

  const roomMoneyTotals = useMemo(
    () => ({
      rate: sumMoneyByCurrency(roomRows, "nightlyRate"),
      value: sumMoneyByCurrency(roomRows, "roomNightValue"),
    }),
    [roomRows]
  );

  const guestRows = useMemo(() => {
    const withStay = (report?.guestsWithStay || []).map((row) => ({
      ...row,
      coverage: "with",
    }));
    const withoutStay = (report?.guestsWithoutStay || []).map((row) => ({
      ...row,
      coverage: "without",
      hotelName: "—",
      roomNumbers: [],
      stayStatus: "—",
      checkInDate: "",
      checkOutDate: "",
      attendeeNames: [],
    }));

    let rows = [];
    if (guestFilter === "with") rows = withStay;
    else if (guestFilter === "without") rows = withoutStay;
    else rows = [...withStay, ...withoutStay];

    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((row) => {
      const attendeeText = (row.attendeeNames || []).join(" ").toLowerCase();
      return (
        String(row.guestName || "").toLowerCase().includes(q) ||
        String(row.hotelName || "").toLowerCase().includes(q) ||
        attendeeText.includes(q) ||
        (row.roomNumbers || []).join(" ").toLowerCase().includes(q)
      );
    });
  }, [report, guestFilter, searchTerm]);

  const handleViewHotel = (hotelId) => {
    const hotel = hotels.find((h) => String(h.hotelId) === String(hotelId));
    if (!hotel) {
      toast.error("Hotel not found.");
      return;
    }
    const mapped = mapStayHotelToPreview(hotel);
    setPreview({
      viewMode: "single",
      hotelName: mapped.hotelName,
      rooms: mapped.rooms,
      metrics: mapped.metrics,
      totalNights: mapped.totalNights,
      isDraft: false,
    });
  };

  const handleViewAllHotels = () => {
    if (!hotels.length) {
      toast.error("No hotels to preview.");
      return;
    }
    setPreview({
      viewMode: "event",
      hotels: hotels.map(mapStayHotelToPreview),
    });
  };

  const handleExportExcel = () => {
    try {
      const eventLabel = safeFileLabel(selectedEvent?.eventName, "Event");
      const eventName = selectedEvent?.eventName || "";

      if (activeView === "rooms") {
        if (!roomRows.length) {
          toast.error("No rooms to export.");
          return;
        }
        exportRowsToExcel({
          rows: roomRows,
          sheetName: "Room Inventory",
          fileName: `${eventLabel}_Room_Inventory.xlsx`,
          reportTitle: "Stay Report — Room Inventory",
          eventName,
          columns: [
            { label: "Hotel", value: (r) => r.hotelName || "", width: 24 },
            { label: "Room No.", value: (r) => r.roomNumber || "", width: 10 },
            { label: "Room Type", value: (r) => r.roomType || "", width: 14 },
            { label: "Bed Type", value: (r) => r.bedType || "", width: 12 },
            {
              label: "Room Status",
              value: (r) =>
                r.status === "occupied" ? "Taken" : "Available",
              width: 12,
            },
            {
              label: "Guest Name",
              value: (r) => r.guestName || "",
              width: 22,
            },
            {
              label: "Stay Status",
              value: (r) => displayStayStatus(r.stayStatus),
              width: 14,
            },
            {
              label: "Check-in Date",
              value: (r) => formatDate(r.checkInDate, ""),
              width: 12,
            },
            {
              label: "Check-out Date",
              value: (r) => formatDate(r.checkOutDate, ""),
              width: 12,
            },
            { label: "Nights", value: (r) => r.nights ?? "", width: 10 },
            { label: "Currency", value: (r) => r.currency || "", width: 10 },
            {
              label: "Nightly Rate",
              value: (r) => r.nightlyRate ?? "",
              width: 12,
            },
            {
              label: "Stay Value (Rate × Nights)",
              value: (r) => r.roomNightValue ?? "",
              width: 18,
            },
          ],
        });
      } else {
        if (!guestRows.length) {
          toast.error("No guests to export.");
          return;
        }
        exportRowsToExcel({
          rows: guestRows,
          sheetName: "Guest Coverage",
          fileName: `${eventLabel}_Guest_Coverage.xlsx`,
          reportTitle: "Stay Report — Guest Coverage",
          eventName,
          columns: [
            {
              label: "Main Guest",
              value: (r) => r.guestName || "",
              width: 24,
            },
            {
              label: "Stay Coverage",
              value: (r) =>
                r.coverage === "with" ? "With stay" : "No stay",
              width: 12,
            },
            { label: "Hotel", value: (r) => r.hotelName || "", width: 22 },
            {
              label: "Room No(s).",
              value: (r) => (r.roomNumbers || []).join(", "),
              width: 14,
            },
            {
              label: "Stay Status",
              value: (r) => displayStayStatus(r.stayStatus),
              width: 14,
            },
            {
              label: "Attendees in Room",
              value: (r) => (r.attendeeNames || []).join(", "),
              width: 28,
            },
            {
              label: "Check-in Date",
              value: (r) => formatDate(r.checkInDate, ""),
              width: 12,
            },
            {
              label: "Check-out Date",
              value: (r) => formatDate(r.checkOutDate, ""),
              width: 12,
            },
            { label: "Nights", value: (r) => r.nights ?? "", width: 10 },
            { label: "Currency", value: (r) => r.currency || "", width: 10 },
            {
              label: "Room Rate Total",
              value: (r) => r.roomRateTotal ?? "",
              width: 14,
            },
          ],
        });
      }

      toast.success("Export successful!");
    } catch (error) {
      console.error("Stay report export failed:", error);
      toast.error("Failed to export stay report.");
    }
  };

  if (!selectedEvent?.eventId) {
    return (
      <div className="container-fluid stay-report-page">
        <div className="occasion-top occasion-top-with-sidebar row g-4 align-items-center">
          <div className="col-lg-9">
            <h2 className="mb-0">Stay Report</h2>
          </div>
        </div>
        <div className="stay-report-empty-state">
          <p>Select an event to view room availability and guest stay coverage.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid stay-report-page">
      <div className="occasion-top occasion-top-with-sidebar row g-4 align-items-center">
        <div className="col-12 stay-report-title-row">
          <h2 className="mb-0">Stay Report</h2>
          <div className="stay-report-title-actions">
            <button
              type="button"
              className="btn stay-report-action-btn stay-report-icon-btn"
              onClick={loadReport}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn stay-report-action-btn stay-report-export-btn"
              onClick={handleExportExcel}
              disabled={loading || !report}
            >
              Export to Excel
              <i className="bi bi-download" aria-hidden="true" />
            </button>
            <Link
              to="/travel-and-stay"
              className="btn stay-report-action-btn stay-report-link-btn"
            >
              Open Travel & Stay
            </Link>
          </div>
        </div>
      </div>

      {loading && !report ? (
        <p className="stay-report-loading">Loading stay report…</p>
      ) : (
        <>
          <section className="stay-report-metrics stay-report-metrics-6" aria-label="Summary metrics">
            <MetricCard
              label="Hotels"
              value={summary?.hotels ?? hotels.length ?? 0}
              hint="Configured for event"
              tone="is-covered"
            />
            <MetricCard
              label="Total Rooms"
              value={summary?.totalRooms ?? 0}
              hint={`${summary?.occupancyPercent ?? 0}% occupancy`}
            />
            <MetricCard
              label="Available"
              value={summary?.availableRooms ?? 0}
              hint="Ready to assign"
              tone="is-available"
            />
            <MetricCard
              label="Occupied"
              value={summary?.occupiedRooms ?? 0}
              hint="Currently taken"
              tone="is-occupied"
            />
            <MetricCard
              label="Inventory Value"
              value={formatSpendList(summary?.inventoryValueByCurrency)}
              hint="All rooms × hotel nights"
            />
            <MetricCard
              label="Committed Spend"
              value={formatSpendList(summary?.committedSpendByCurrency)}
              hint="Assigned rooms × stay nights"
              tone="is-occupied"
            />
          </section>

          <div className="stay-report-toolbar">
            <div className="stay-report-view-tabs" role="tablist">
              {VIEW_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeView === tab.id}
                  className={`stay-report-view-tab${
                    activeView === tab.id ? " is-active" : ""
                  }`}
                  onClick={() => setActiveView(tab.id)}
                >
                  <i className={`bi ${tab.icon}`} aria-hidden="true" />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="stay-report-filters">
              {activeView === "rooms" ? (
                <div className="stay-report-chip-group">
                  {[
                    { id: "all", label: "All rooms" },
                    { id: "available", label: "Available" },
                    { id: "occupied", label: "Taken" },
                  ].map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`stay-report-chip${
                        roomFilter === chip.id ? " is-active" : ""
                      }`}
                      onClick={() => setRoomFilter(chip.id)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="stay-report-chip-group">
                  {GUEST_FILTERS.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`stay-report-chip${
                        guestFilter === chip.id ? " is-active" : ""
                      }`}
                      onClick={() => setGuestFilter(chip.id)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}
              <input
                type="search"
                className="form-control stay-report-search"
                placeholder={
                  activeView === "rooms"
                    ? "Search room, hotel, guest…"
                    : "Search guest, hotel…"
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {activeView === "rooms" ? (
            <section className="stay-report-section">
              <div className="stay-report-table-wrap is-hotels-compact">
                <div className="stay-report-table-head">
                  <h3>Hotels</h3>
                  <div className="stay-report-table-head-actions">
                    <span>{hotels.length} hotels</span>
                    <button
                      type="button"
                      className="btn stay-report-action-btn stay-report-icon-btn"
                      onClick={handleViewAllHotels}
                      disabled={!hotels.length}
                      title="View all hotel cards"
                      aria-label="View all hotel cards"
                    >
                      <i className="bi bi-eye" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="stay-report-table-scroll">
                  <table className="table stay-report-table stay-report-hotels-table">
                    <thead>
                      <tr>
                        <th>Sr</th>
                        <th>Hotel</th>
                        <th>Rooms</th>
                        <th>Taken</th>
                        <th>Available</th>
                        <th>Nights</th>
                        <th>Inventory Value</th>
                        <th>Committed Spend</th>
                        <th className="text-center">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotels.length ? (
                        hotels.map((hotel, index) => (
                          <tr key={hotel.hotelId}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{hotel.hotelName}</strong>
                            </td>
                            <td>{hotel.totalRooms ?? 0}</td>
                            <td>{hotel.occupied ?? 0}</td>
                            <td>{hotel.available ?? 0}</td>
                            <td>{hotel.totalNights ?? "—"}</td>
                            <td>
                              {formatSpendList(hotel.inventoryValueByCurrency)}
                            </td>
                            <td>
                              {formatSpendList(hotel.committedSpendByCurrency)}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn stay-report-action-btn stay-report-icon-btn"
                                title="View hotel card"
                                aria-label={`View ${hotel.hotelName}`}
                                onClick={() => handleViewHotel(hotel.hotelId)}
                              >
                                <i className="bi bi-eye" aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No hotels configured for this event.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="stay-report-table-wrap">
                <div className="stay-report-table-head">
                  <h3>All rooms</h3>
                  <span>{roomRows.length} rooms</span>
                </div>
                <div className="stay-report-table-scroll">
                  <table className="table stay-report-table">
                    <thead>
                      <tr>
                        <th>Hotel</th>
                        <th>Room</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Guest</th>
                        <th>Stay</th>
                        <th>Check-in</th>
                        <th>Check-out</th>
                        <th>Nights</th>
                        <th className="stay-report-money-col">Night rate</th>
                        <th className="stay-report-money-col">Stay total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roomGroups.length ? (
                        roomGroups.map((group) => (
                          <React.Fragment key={group.key}>
                            <tr className="stay-report-type-row">
                              <td colSpan={9}>
                                {group.label}
                                <em> · {group.items.length} rooms</em>
                              </td>
                              <td className="stay-report-money-col stay-report-type-total">
                                {group.rateTotalLabel !== "—"
                                  ? group.rateTotalLabel
                                  : "—"}
                              </td>
                              <td className="stay-report-money-col stay-report-type-total">
                                {group.valueTotalLabel !== "—"
                                  ? group.valueTotalLabel
                                  : "—"}
                              </td>
                            </tr>
                            {group.items.map((room) => {
                              const checkedIn = isCheckedInStatus(
                                room.stayStatus
                              );
                              const checkedOut = isCheckedOutStatus(
                                room.stayStatus
                              );
                              return (
                                <tr key={`${room.hotelId}-${room.roomId}`}>
                                  <td>{room.hotelName}</td>
                                  <td>
                                    <strong>{room.roomNumber}</strong>
                                  </td>
                                  <td>{room.roomType || "—"}</td>
                                  <td>
                                    <span
                                      className={`stay-report-status is-${room.status}`}
                                    >
                                      {room.status === "occupied"
                                        ? "Taken"
                                        : "Available"}
                                    </span>
                                  </td>
                                  <td>{room.guestName || "—"}</td>
                                  <td>{room.stayStatus || "—"}</td>
                                  <td
                                    className={`stay-report-date-cell${
                                      checkedIn || checkedOut
                                        ? " is-checked-in"
                                        : ""
                                    }`}
                                  >
                                    {formatDate(room.checkInDate)}
                                  </td>
                                  <td
                                    className={`stay-report-date-cell${
                                      checkedOut ? " is-checked-out" : ""
                                    }`}
                                  >
                                    {formatDate(room.checkOutDate)}
                                  </td>
                                  <td>{room.nights ?? "—"}</td>
                                  <td className="stay-report-money-col">
                                    {formatMoney(
                                      room.nightlyRate,
                                      room.currency
                                    )}
                                  </td>
                                  <td className="stay-report-money-col">
                                    {formatMoney(
                                      room.roomNightValue,
                                      room.currency
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="text-center">
                            No rooms match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {roomGroups.length && roomMoneyTotals.value !== "—" ? (
                      <tfoot>
                        <tr className="stay-report-total-row">
                          <td colSpan={9}>
                            <strong>Total</strong>
                            <em> · {roomRows.length} rooms</em>
                          </td>
                          <td className="stay-report-money-col">
                            <strong>{roomMoneyTotals.rate}</strong>
                          </td>
                          <td className="stay-report-money-col">
                            <strong>{roomMoneyTotals.value}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            </section>
          ) : (
            <section className="stay-report-section">
              <div className="stay-report-guest-split">
                <article className="stay-report-guest-stat is-covered">
                  <em>Allocated with stay</em>
                  <strong>{summary?.withStay ?? 0}</strong>
                  <span>
                    {summary?.coveragePercent ?? 0}% of{" "}
                    {summary?.totalGuests ?? 0} guests
                  </span>
                </article>
                <article className="stay-report-guest-stat is-gap">
                  <em>Still need stay</em>
                  <strong>{summary?.withoutStay ?? 0}</strong>
                  <span>Guests without an accommodation record</span>
                </article>
                <article className="stay-report-guest-stat is-covered">
                  <em>Committed spend</em>
                  <strong>
                    {formatSpendList(summary?.committedSpendByCurrency)}
                  </strong>
                  <span>Rate × nights for assigned stays</span>
                </article>
              </div>

              <div className="stay-report-table-wrap">
                <div className="stay-report-table-head">
                  <h3>Guest stay list</h3>
                  <span>{guestRows.length} guests</span>
                </div>
                <div className="stay-report-table-scroll">
                  <table className="table stay-report-table">
                    <thead>
                      <tr>
                        <th>Sr No.</th>
                        <th>Main Guest</th>
                        <th>Coverage</th>
                        <th>Hotel</th>
                        <th>Room(s)</th>
                        <th>Status</th>
                        <th>Attendees</th>
                        <th>Check-in</th>
                        <th>Check-out</th>
                        <th>Nights</th>
                        <th>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guestRows.length ? (
                        guestRows.map((row, index) => {
                          const checkedIn = isCheckedInStatus(row.stayStatus);
                          const checkedOut = isCheckedOutStatus(
                            row.stayStatus
                          );
                          return (
                            <tr
                              key={`${row.coverage}-${row.guestId}-${
                                row.accommodationId || "none"
                              }`}
                            >
                              <td>{index + 1}</td>
                              <td>
                                <strong>{row.guestName}</strong>
                              </td>
                              <td>
                                <span
                                  className={`stay-report-status is-${
                                    row.coverage === "with"
                                      ? "occupied"
                                      : "available"
                                  }`}
                                >
                                  {row.coverage === "with"
                                    ? "With stay"
                                    : "No stay"}
                                </span>
                              </td>
                              <td>{row.hotelName || "—"}</td>
                              <td>
                                {(row.roomNumbers || []).join(", ") || "—"}
                              </td>
                              <td>{row.stayStatus || "—"}</td>
                              <td>
                                {(row.attendeeNames || []).join(", ") || "—"}
                              </td>
                              <td
                                className={`stay-report-date-cell${
                                  checkedIn || checkedOut
                                    ? " is-checked-in"
                                    : ""
                                }`}
                              >
                                {formatDate(row.checkInDate)}
                              </td>
                              <td
                                className={`stay-report-date-cell${
                                  checkedOut ? " is-checked-out" : ""
                                }`}
                              >
                                {formatDate(row.checkOutDate)}
                              </td>
                              <td>{row.nights ?? "—"}</td>
                              <td>
                                {row.coverage === "with"
                                  ? formatMoney(row.roomRateTotal, row.currency)
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center">
                            No guests match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <HotelRoomPreviewModal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        viewMode={preview?.viewMode || "single"}
        eventName={selectedEvent?.eventName || ""}
        hotels={preview?.viewMode === "event" ? preview.hotels || [] : []}
        hotelName={preview?.viewMode === "single" ? preview.hotelName : ""}
        address={preview?.viewMode === "single" ? preview.address : undefined}
        rooms={preview?.viewMode === "single" ? preview.rooms || [] : []}
        metrics={preview?.viewMode === "single" ? preview.metrics : undefined}
        totalNights={
          preview?.viewMode === "single" ? preview.totalNights : undefined
        }
        isDraft={preview?.viewMode === "single" ? preview.isDraft : false}
      />
    </div>
  );
};

export default StayReports;
